import Map from 'ol/Map.js';
import View from 'ol/View.js';
import Projection from 'ol/proj/Projection.js';
import * as Units from 'ol/proj/Units.js';
import ImageLayer from 'ol/layer/Image';
import Static from 'ol/source/ImageStatic';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature.js';
import Polygon from 'ol/geom/Polygon.js';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point.js';
import Style from 'ol/style/Style.js';
import Icon from 'ol/style/Icon.js';
import Fill from 'ol/style/Fill.js';
import Stroke from 'ol/style/Stroke.js';
import Text from 'ol/style/Text';
import { defaults } from 'ol/control/defaults';
import { defaults as defaultInteractions } from 'ol/interaction/defaults';
import * as turf from '@turf/turf';
import { iconHighlightMap, iconMap, LayerId, OFFLINE_ICON } from './constant';

export const Z_INDEX = {
  // 底层 - 地图底图和背景 (0-19)
  BASE_MAP: 0, // 地图底图
  IMAGE_LAYER: 10, // 图片图层

  // 中层 - 基础几何图形 (20-39)
  POLYGON: 20, // 多边形区域
  LINE_STRING: 30, // 普通线条路径

  // 上层 - 重要业务元素 (40-59)
  SCHEDULED_ROUTE: 40, // 计划路线
  DEVICE_POINTS: 50, // 设备点位

  // 顶层 - 交互和装饰元素 (60-89)
  ARROWS: 60, // 方向箭头
  LABELS: 70, // 文字标签
  INTERACTIVE: 80, // 交互元素

  // 最顶层 - 临时和高优先级元素 (90-100)
  TEMPORARY: 90, // 临时图层
  OVERLAY: 100, // 覆盖层
} as const;

export interface RobotMapToolProps {
  container: HTMLElement;
  projection: any;
  center: any;
  rotation: number;
  defaultZoom?: number;
}
export const generateProjection = (
  extent: [number, number, number, number]
) => {
  return new Projection({
    code: '2249',
    units: Units.fromCode(9001),
    extent: extent,
  });
};

export class RobotMapTool {
  map: any;
  container: HTMLElement;
  center: any;
  rotation: number;
  scheduledPathFeature: any;
  defaultZoom: number;
  featureMap: { [key: string]: Feature };
  // 箭头图层枚举
  constructor(options: RobotMapToolProps) {
    this.container = options.container;
    this.center = options.center;
    this.rotation = options.rotation;
    this.defaultZoom = options.defaultZoom || 1;
    this.featureMap = {};
    this.initMap(options.projection);
    this.initMapEvent();
  }

  initMap(projection: any) {
    // 获取默认交互，然后移除双击缩放
    const interactions = defaultInteractions({
      doubleClickZoom: false, // 禁用双击缩放
    });

    this.map = new Map({
      target: this.container,
      controls: defaults({
        zoom: false,
        rotate: false,
      }),
      interactions: interactions, // 使用自定义的交互配置
      view: new View({
        projection: projection,
        center: this.center,
        rotation: this.rotation,
        zoom: this.defaultZoom,
        maxZoom: 17,
        minZoom: 1,
      }),
    });
  }

  initMapEvent() {
    this.map.on('click', (e: any) => {
      // 检测点击的feature
      const result = this.map.forEachFeatureAtPixel(
        e.pixel,
        (feature: any, layer: any) => {
          const layerId = layer.get('layerId');
          const onFeatureClick = layer.get('onFeatureClick');

          // 如果是设备点位图层
          if (
            layerId === LayerId.DEVICE_POINT ||
            layerId === LayerId.POINT_LABEL
          ) {
            if (onFeatureClick) {
              const deviceName = feature.get('deviceName');
              const remarkName = feature.get('remarkName');
              onFeatureClick(feature, deviceName, remarkName);
              return true;
            }
          }

          return false;
        }
      );
    });
  }

  /**
   * 创建图片图层
   * @param url
   * @param projection
   * @param extent
   * @param layerId
   * @returns
   */
  createImgLayer(
    url: string,
    projection: any,
    extent: [number, number, number, number],
    layerId: string
  ) {
    const _imgLayer = new ImageLayer({
      zIndex: Z_INDEX.IMAGE_LAYER,
      source: new Static({
        url: url,
        projection: projection,
        imageExtent: extent,
      }),
    });
    _imgLayer.set('layerId', layerId);
    this.map.addLayer(_imgLayer);
    return _imgLayer;
  }

  /**
   * 创建矢量图层
   * @param opts 图层配置 style zIndex等
   * @param {string} layerId 图层Id
   * @param sourceData
   * @param type
   */
  createVectorLayer(
    opts: any,
    layerId: string,
    sourceData?: {
      type: 'Point' | 'LineString' | 'Polygon';
      coordinates: [number, number][];
      id?: string | number;
      iconUrl?: string;
      name?: string;
      otherStyle?: any;
      deviceName?: string;
      remarkName?: string;
      labelText?: string;
      selectedStyle?: any;
      labelStyle?: any;
      isSelected?: boolean;
      onlineStatus?: number;
    }[],
    onFeatureClick?: (feature: any, deviceName?: string) => void
  ) {
    const _source = new VectorSource();
    if (sourceData && sourceData?.length > 0) {
      sourceData.forEach((val, index) => {
        let _feature: any;
        switch (val.type) {
          case 'LineString':
            _feature = this.createLine(
              val.coordinates,
              val.id ?? index + val.type,
              'solid',
              val.otherStyle // 传递线条样式
            );
            break;
          case 'Point':
            _feature = this.createPointFeature({
              coordinates: val.coordinates,
              pointId: val.id ?? index + val.type,
              imgSrc: val.iconUrl,
              name: val.name,
              otherStyle: val.otherStyle,
              labelStyle: val.labelStyle, // 传递label样式配置
              deviceName: val.deviceName, // 传递设备名称
              remarkName: val.remarkName,
              labelText: val.labelText,
              onlineStatus: val.onlineStatus,
            });
            break;
          case 'Polygon':
            _feature = this.createPolygon(
              val.coordinates,
              val.id ?? index + val.type
            );
            break;
        }
        _source.addFeature(_feature);
        this.featureMap[_feature.getId()] = _feature;
      });
    }
    const _vectorLayer = new VectorLayer({
      ...opts,
      source: _source,
    });
    _vectorLayer.set('layerId', layerId);

    // 如果有点击事件回调，添加点击监听器
    if (onFeatureClick) {
      _vectorLayer.set('onFeatureClick', onFeatureClick);
    }

    this.map.addLayer(_vectorLayer);
    return _vectorLayer;
  }

  /**
   * 创建点的feature
   * @param {Array} coordinates 点的位置
   * @param {string} imgSrc 图片地址
   * @param {string} name 点的名称
   * @param {string | number} pointId 点的id
   * @param {object} otherStyle 点的其他样式
   * @param {object} labelStyle label框样式配置
   */
  createPointFeature = ({
    coordinates,
    pointId,
    imgSrc,
    name,
    otherStyle,
    labelStyle,
    deviceName,
    remarkName,
    labelText,
    onlineStatus,
  }: {
    coordinates: any;
    pointId: string | number;
    imgSrc?: string;
    name?: string;
    otherStyle?: any;
    labelStyle?: any;
    deviceName?: string;
    remarkName?: string;
    labelText?: string;
    onlineStatus?: number;
  }) => {
    const pointFeature = new Feature({
      geometry: new Point(coordinates),
      name: pointId,
      zIndex: labelStyle?.showLabel ? Z_INDEX.LABELS : Z_INDEX.DEVICE_POINTS, // 有label的点使用更高的zIndex
    });
    pointFeature.setId(pointId);
    pointFeature.set('onlineStatus', onlineStatus);
    if (deviceName) {
      pointFeature.set('deviceName', deviceName);
    }
    if (remarkName) {
      pointFeature.set('remarkName', remarkName);
    }
    if (labelText) {
      pointFeature.set('labelText', labelText);
    }

    const iconStyle = imgSrc
      ? new Icon({
          ...otherStyle,
          src: imgSrc,
          rotation: otherStyle?.rotation
            ? (otherStyle.rotation * Math.PI) / 180
            : 0,
        })
      : undefined;

    const style = new Style({
      image: iconStyle,
      text: labelStyle?.showLabel
        ? labelStyle?.textStyle
          ? labelStyle?.textStyle
          : (() => {
              return new Text({
                text: labelStyle.text || name || '',
                font: '16px "PingFang SC", Arial, sans-serif',
                offsetY: -24, // 在设备图标上方显示
                fill: new Fill({
                  color: '#ffffff', // 白色文字
                }),
                backgroundFill: new Fill({
                  color: 'rgba(255, 77, 79, 0.95)', // 红色半透明背景
                }),
                backgroundStroke: new Stroke({
                  color: 'rgba(255, 77, 79, 0.95)',
                  width: 1,
                  lineCap: 'round',
                  lineJoin: 'round',
                }),
                padding: [8, 8, 8, 8], // 内边距
                // 通过设置文本渲染函数来实现圆角效果
                textBaseline: 'middle',
                textAlign: 'center',
              });
            })()
        : name
          ? new Text({
              text: name,
              font: '13px "PingFang SC", Arial, sans-serif',
              fill: new Fill({
                color: 'rgba(255, 77, 79, 0.95)',
              }),
            })
          : undefined,
    });
    pointFeature.setStyle(style);
    return pointFeature;
  };

  /**
   * 创建线的feature
   * @param {Array} coordinates 线的位置
   * @param {string | number} type
   * @param {string} lineId 线的id
   * @param {object} otherStyle 点的其他样式
   */
  createLine(
    coordinates: any,
    lineId: string | number,
    type?: 'dash' | 'solid',
    lineStyle?: any
  ) {
    const lineFeature = new Feature({
      geometry: new LineString(coordinates),
      zIndex: Z_INDEX.LINE_STRING,
      name: lineId,
    });

    // 支持带边框的线条样式
    if (lineStyle && lineStyle.borderColor && lineStyle.borderWidth) {
      const { color, width, borderColor, borderWidth } = lineStyle;

      // 创建多层样式数组
      const styles = [];

      // 先添加边框样式（更宽的线条）
      styles.push(
        new Style({
          stroke: new Stroke({
            color: borderColor,
            width: (width || 2) + borderWidth * 2, // 主线宽度 + 边框宽度*2
            lineDash: type === 'dash' ? [4, 4, 4, 4] : undefined,
          }),
        })
      );

      // 添加主线条样式
      styles.push(
        new Style({
          stroke: new Stroke({
            color: color || '#00ff00',
            width: width || 2,
            lineDash: type === 'dash' ? [4, 4, 4, 4] : undefined,
          }),
        })
      );

      lineFeature.setStyle(styles);
    } else {
      // 兼容原有的简单样式
      const style = new Style({
        stroke: new Stroke({
          lineDash: type === 'dash' ? [4, 4, 4, 4] : [1, 1, 1, 1, 1, 1],
          color: lineStyle?.color || 'blue',
          width: lineStyle?.width || 2,
        }),
      });
      lineFeature.setStyle(style);
    }

    return lineFeature;
  }

  /**
   * 创建多边形的feature
   * @param {Array} coordinates 线的位置
   * @param {string | number} polygonId 线的id
   * @param {object} otherStyle 点的其他样式
   */
  createPolygon = (
    coordinates: any,
    polygonId: string | number,
    otherStyle?: any
  ) => {
    const polygonFeature = new Feature({
      geometry: new Polygon(coordinates),
      zIndex: Z_INDEX.POLYGON,
    });
    polygonFeature.setId(polygonId);
    const style = new Style({
      fill: new Fill({
        // 填充样式
        color: 'rgba(255, 87, 77, 0.4)',
      }),
      ...otherStyle,
    });
    polygonFeature.setStyle(style);
    return polygonFeature;
  };

  /**
   * 获取图层
   * @param {string | undefined | null} layerId 图层Id
   */
  getLayer = (layerId: string | undefined | null) => {
    if (!layerId) {
      return null;
    }
    let layers = this.map.getLayers().getArray();
    for (let l of layers) {
      if (l.get('layerId') === layerId) {
        return l;
      }
    }
    return null;
  };
  /**
   * 获取图层的source
   * @param {string | undefined | null} layerId 图层Id
   */
  getSource = (layerId: string | undefined | null) => {
    const source = this.getLayer(layerId).getSource();
    return source;
  };

  /**
   * 改变图层显隐
   * @param layerId 图层Id
   * @param visible 是否显示 false不显示 true显示
   */
  changeLayerVisible = (layerId: any, visible: boolean) => {
    this.getLayer(layerId)?.setVisible(visible);
  };

  /**
   * 在路径上添加箭头标记
   * @param pathFeature 路径特性
   * @param layerEnum 图层枚举
   * @param pathColor 路径颜色（未使用，保留参数以兼容现有调用）
   * @param arrowsArray 箭头数组引用
   * @param spacing 箭头间距（米）
   */
  private addArrowsToPath(
    pathFeature: Feature,
    layerEnum: string,
    _pathColor: string, // 添加下划线前缀表示未使用的参数
    arrowsArray: Feature[],
    spacing: number = 200
  ) {
    // 清除现有箭头
    this.clearArrows(arrowsArray);
    if (!pathFeature) return;
    try {
      // 获取路径几何
      const lineGeometry = pathFeature.getGeometry() as LineString;
      if (!lineGeometry) return;
      const coordinates = lineGeometry.getCoordinates();
      const line = turf.lineString(coordinates);
      // 获取路径长度
      const lineLength = turf.length(line) * 1000;

      let numArrows = 0;
      if (lineLength > 1000) {
        numArrows = Math.floor(lineLength / 200);
      } else if (lineLength > 500) {
        numArrows = Math.floor(lineLength / 100);
      } else if (lineLength > 200) {
        numArrows = Math.floor(lineLength / 50);
      } else if (lineLength > 100) {
        numArrows = Math.floor(lineLength / 25);
      } else {
        numArrows = 5;
      }
      const maxArrows = 20;
      const actualNumArrows = Math.min(numArrows, maxArrows);
      // 箭头图层配置
      const arrowLayerName = LayerId.SCHEDULED_ARROW_LAYER;
      for (let i = 1; i < actualNumArrows; i++) {
        const fraction = i / actualNumArrows;
        const arrowCoord = lineGeometry.getCoordinateAt(fraction);
        const prevFraction = Math.max(0, fraction - 0.01);
        const nextFraction = Math.min(1, fraction + 0.01);
        const prevCoord = lineGeometry.getCoordinateAt(prevFraction);
        const nextCoord = lineGeometry.getCoordinateAt(nextFraction);
        const dx = nextCoord[0] - prevCoord[0];
        const dy = nextCoord[1] - prevCoord[1];
        const rotation = Math.atan2(dy, dx);
        const arrowAngle = rotation - Math.PI / 2;
        const arrowStyle = new Style({
          image: new Icon({
            src: require('@warehouse/assets/common/path-arrow.svg'),
            anchor: [0.5, 0.5],
            scale: 0.5, // 适当缩小图标
            rotateWithView: true,
            rotation: -arrowAngle,
          }),
        });

        const arrowFeature = new Feature({
          geometry: new Point(arrowCoord),
          name: `arrow-${layerEnum}-${i}`,
        });

        arrowFeature.setStyle(arrowStyle);
        arrowsArray.push(arrowFeature);
      }
    } catch (error) {}
  }

  /**
   * 清除箭头
   * @param arrowsArray 箭头数组
   * @param layerEnum 图层枚举，用于确定箭头所在图层
   */
  private clearArrows(arrowsArray: Feature[]) {
    // 获取箭头图层
    const arrowLayer = this.getLayer(LayerId.SCHEDULED_ARROW_LAYER);
    if (!arrowLayer) return;
    if (arrowsArray.length > 0) {
      const source = arrowLayer.getSource();
      arrowsArray.forEach((arrow) => {
        source.removeFeature(arrow);
      });
      arrowsArray.length = 0;
    }
  }

  removeLayerById(layerId: string) {
    this.map.removeLayer(this.getLayer(layerId));
  }

  /**
   * 更新地图中心点
   * @param center 新的中心点坐标 [x, y]
   * @param duration 动画持续时间(毫秒)，默认1000ms
   */
  drawScheduledPath(coordinates: [number, number][]) {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return;
    }

    const validCoordinates = coordinates.filter(
      (item) =>
        item && typeof item?.[0] === 'number' && typeof item?.[1] === 'number'
    );

    if (validCoordinates.length === 0) {
      return;
    }

    // 清除现有的路线和箭头图层
    if (this.getLayer(LayerId.SCHEDULED_ROUTE_LAYER)) {
      this.removeLayerById(LayerId.SCHEDULED_ROUTE_LAYER);
    }
    if (this.getLayer(LayerId.SCHEDULED_ARROW_LAYER)) {
      this.removeLayerById(LayerId.SCHEDULED_ARROW_LAYER);
    }

    // 创建路线图层
    const routeLayer = this.createVectorLayer(
      {
        zIndex: Z_INDEX.SCHEDULED_ROUTE,
      },
      LayerId.SCHEDULED_ROUTE_LAYER,
      [
        {
          type: 'LineString',
          coordinates: validCoordinates,
          id: LayerId.SCHEDULED_ROUTE_LAYER,
          name: 'scheduled-path',
          otherStyle: {
            color: '#12B35D',
            width: 8,
            borderColor: '#0B723B',
            borderWidth: 1,
          },
        },
      ]
    );

    // 获取创建的路线feature并添加箭头
    const routeSource = routeLayer.getSource();
    if (routeSource) {
      const pathFeature = routeSource.getFeatures()[0];

      if (pathFeature) {
        // 创建箭头数组来存储箭头features
        const arrowsArray: Feature[] = [];

        // 调用addArrowsToPath方法添加箭头
        this.addArrowsToPath(
          pathFeature,
          LayerId.SCHEDULED_ROUTE_LAYER,
          '#12B35D', // 路径颜色
          arrowsArray,
          200 // 箭头间距
        );

        // 将箭头features添加到箭头图层
        if (arrowsArray.length > 0) {
          let arrowLayer = this.getLayer(LayerId.SCHEDULED_ARROW_LAYER);

          // 如果箭头图层不存在，先创建它
          if (!arrowLayer) {
            arrowLayer = this.createVectorLayer(
              { zIndex: Z_INDEX.ARROWS },
              LayerId.SCHEDULED_ARROW_LAYER
            );
          }

          if (arrowLayer) {
            const arrowSource = arrowLayer.getSource();
            if (arrowSource) {
              arrowsArray.forEach((arrow) => {
                arrowSource.addFeature(arrow);
              });
            }
          }
        }
      }
    }
  }
  updateCenter(center: [number, number], duration: number = 1000) {
    const view = this.map.getView();

    if (duration > 0) {
      // 带动画的中心点更新
      view.animate({
        center: center,
        duration: duration,
      });
    } else {
      // 立即更新中心点
      view.setCenter(center);
    }

    // 更新实例的center属性
    this.center = center;
  }

  /**
   * 更新设备选中状态
   * @param deviceName 设备名称
   * @param isSelected 是否选中
   */
  updateDeviceSelection(deviceName: string, isSelected: boolean) {
    const deviceLayer = this.getLayer(LayerId.DEVICE_POINT);
    if (deviceLayer) {
      const source = deviceLayer.getSource();
      if (source) {
        const features = source.getFeatures();
        features.forEach((feature: any) => {
          if (feature.get('deviceName') === deviceName) {
            feature.set('isSelected', isSelected);
            const onlineStatus = feature.get('onlineStatus');
            // 获取设备的告警状态
            const featureId = feature.getId();
            let alarmState = 'NORMAL';
            if (featureId && typeof featureId === 'string') {
              if (featureId.includes('CRITICAL')) alarmState = 'CRITICAL';
              else if (featureId.includes('SEVERITY')) alarmState = 'SEVERITY';
            }
            // 根据选中状态选择图标
            const iconUrl =
              onlineStatus == 0
                ? OFFLINE_ICON
                : isSelected
                  ? iconHighlightMap.get(
                      alarmState as keyof typeof iconHighlightMap
                    )
                  : iconMap.get(alarmState as keyof typeof iconMap);

            // 获取当前旋转角度
            const currentStyle = feature.getStyle();
            let rotation = 0;
            if (currentStyle && currentStyle.getImage()) {
              rotation = currentStyle.getImage().getRotation();
            }

            // 创建新样式
            const newStyle = new Style({
              image: new Icon({
                src: iconUrl,
                width: onlineStatus == 0 ? 44 : isSelected ? 67 : 44,
                height: onlineStatus == 0 ? 37 : isSelected ? 67 : 37,
                rotation: rotation,
              }),
            });

            feature.setStyle(newStyle);
            feature.changed();
          }
        });
      }
    }
  }

  /**
   * 显示设备label
   * @param deviceName 设备名称
   * @param onLabelClick label点击回调
   */
  showDeviceLabel(
    deviceName: string,
    labelText: string,
    coordinates: [number, number][],
    onLabelClick: (deviceName: string) => void
  ) {
    this.createVectorLayer(
      {
        zIndex: 60,
      },
      LayerId.POINT_LABEL,
      [
        {
          type: 'Point',
          coordinates: coordinates,
          deviceName: deviceName,
          id: deviceName + '_label',
          labelStyle: {
            text: labelText,
            showLabel: true,
            textStyle: new Text({
              text: labelText,
              font: '12px Arial',
              offsetY: -35, // 在设备图标上方显示
              fill: new Fill({
                color: '#ffffff', // 白色文字
              }),
              backgroundFill: new Fill({
                color: 'rgba(0, 0, 0, 0.8)', // 黑色半透明背景
              }),
              padding: [8, 8, 8, 8], // 内边距
            }),
          },
        },
      ],
      onLabelClick
    );
  }

  /**
   * 更新设备label位置
   * @param deviceName 设备名称
   * @param coordinates 新的坐标位置
   */
  updateDeviceLabelPosition(deviceName: string, coordinates: [number, number]) {
    const labelLayer = this.getLayer(LayerId.POINT_LABEL);
    if (labelLayer) {
      const source = labelLayer.getSource();
      if (source) {
        const features = source.getFeatures();
        features.forEach((feature: any) => {
          if (feature.get('deviceName') === deviceName) {
            // 更新feature的几何位置
            feature.setGeometry(new Point(coordinates));
          }
        });
      }
    }
  }
 
  /**
   * 移除指定地图的已调度路径
   * @param map 地图对象
   */
  removeScheduledPath() {
    // 清除现有的路线和箭头图层
    if (this.getLayer(LayerId.SCHEDULED_ROUTE_LAYER)) {
      this.removeLayerById(LayerId.SCHEDULED_ROUTE_LAYER);
    }
    if (this.getLayer(LayerId.SCHEDULED_ARROW_LAYER)) {
      this.removeLayerById(LayerId.SCHEDULED_ARROW_LAYER);
    }
  }
}
